import { useCallback, useRef, useState } from 'react';
import { PlayerState, PlayerStatus, PlayerSubStatus, PlayerAction, VALID_TRANSITIONS } from '@/types/playerStates';

export const usePlayerState = () => {
  const [playerState, setPlayerState] = useState<PlayerState>({
    status: 'idle',
    subStatus: {
      isAudioLoading: false,
      isNetworkRequest: false,
      isCacheOperation: false,
      isUserInteraction: false,
    },
    lastAction: 'init',
    timestamp: Date.now(),
  });

  // Timeout de segurança para evitar estados presos
  const stateTimeoutRef = useRef<NodeJS.Timeout>();
  const MAX_STATE_DURATION = 10000; // 10 segundos

  // Validação de transição
  const isValidTransition = useCallback((from: PlayerStatus, to: PlayerStatus): boolean => {
    return VALID_TRANSITIONS[from]?.includes(to) || false;
  }, []);

  const updatePlayerState = useCallback((
    newStatus: PlayerStatus,
    action: string,
    subStatusUpdates?: Partial<PlayerSubStatus>,
    error?: string
  ) => {
    setPlayerState(prevState => {
      // ✅ CORREÇÃO: Evita transições para o mesmo estado
      if (prevState.status === newStatus) {
        // Apenas atualiza subStatus se necessário, mas não faz log de transição
        if (subStatusUpdates) {
          return {
            ...prevState,
            subStatus: {
              ...prevState.subStatus,
              ...subStatusUpdates,
            },
            lastAction: action,
            timestamp: Date.now(),
          };
        }
        return prevState; // Não faz nada se for exatamente o mesmo estado
      }

      // Validação de transição usando o estado atual
      if (!VALID_TRANSITIONS[prevState.status]?.includes(newStatus)) {
        // Transição inválida detectada
        // Permite transições para 'error' e 'idle' como escape
        if (!['error', 'idle'].includes(newStatus)) {
          return prevState; // Retorna estado anterior sem mudança
        }
      }

      // Limpa timeout anterior
      if (stateTimeoutRef.current) {
        clearTimeout(stateTimeoutRef.current);
      }

      const newState: PlayerState = {
        status: newStatus,
        subStatus: {
          ...prevState.subStatus,
          ...subStatusUpdates,
        },
        lastAction: action,
        timestamp: Date.now(),
        error,
      };

      // Timeout de segurança para estados não-finais
      if (!['idle', 'playing', 'paused', 'error'].includes(newStatus)) {
        stateTimeoutRef.current = setTimeout(() => {
          // Estado excedeu timeout, resetando
          setPlayerState(prev => ({
            ...prev,
            status: 'idle',
            lastAction: 'timeout-reset',
            timestamp: Date.now(),
            error: `Timeout em estado: ${newStatus}`,
          }));
        }, MAX_STATE_DURATION);
      }

      return newState;
    });
  }, []);

  // Verificações de estado simplificadas
  const canPerformAction = useCallback((action: PlayerAction): boolean => {
    const currentStatus = playerState.status;
    
    switch (action) {
      case 'play-song':
      case 'play-category':
      case 'play-playlist':
        return ['idle', 'paused', 'error', 'buffering', 'playing', 'loading'].includes(currentStatus);
      
      case 'pause':
        return ['playing', 'buffering'].includes(currentStatus);
      
      case 'stop':
        return ['playing', 'paused', 'buffering'].includes(currentStatus);
      
      case 'navigate':
        return !['error'].includes(currentStatus);
      
      case 'switch-song':
      case 'next':
      case 'previous':
        return ['playing', 'paused', 'idle', 'buffering'].includes(currentStatus);
      
      case 'reset':
        return true; // Reset sempre permitido
      
      default:
        return currentStatus === 'idle';
    }
  }, [playerState.status, playerState.subStatus]);

  const resetState = useCallback((reason: string = 'manual-reset') => {
    if (stateTimeoutRef.current) {
      clearTimeout(stateTimeoutRef.current);
    }
    
    setPlayerState({
      status: 'idle',
      subStatus: {
        isAudioLoading: false,
        isNetworkRequest: false,
        isCacheOperation: false,
        isUserInteraction: false,
      },
      lastAction: reason,
      timestamp: Date.now(),
    });

    // Player state reset
  }, []);

  // Cleanup no unmount
  const cleanup = useCallback(() => {
    if (stateTimeoutRef.current) {
      clearTimeout(stateTimeoutRef.current);
    }
  }, []);

  return {
    playerState,
    updatePlayerState,
    canPerformAction,
    resetState,
    cleanup,
    
    // Getters convenientes (compatibilidade com código existente)
    isIdle: playerState.status === 'idle',
    isLoading: ['loading-song', 'loading-playlist', 'buffering', 'transitioning', 'switching-context'].includes(playerState.status),
    isPlaying: playerState.status === 'playing',
    isPaused: playerState.status === 'paused',
    isNavigating: playerState.status === 'navigating',
    isTransitioning: ['transitioning', 'switching-context', 'buffering'].includes(playerState.status),
    hasError: playerState.status === 'error',
    canInteract: ['idle', 'playing', 'paused'].includes(playerState.status),
    
    // Estados específicos para compatibilidade
    isLoadingPlaylist: playerState.status === 'loading-playlist',
    isLoadingSong: playerState.status === 'loading-song',
    isSwitchingSong: playerState.status === 'transitioning',
    isNavigatingToPlayer: playerState.status === 'navigating',
  };
}; 